import { Float } from '@headlessui-float/react';
import { Popover, PopoverBackdrop, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { cn } from '@theprintbar/shared';
import { SearchCircleIcon, SearchIcon } from '@theprintbar/ui';
import { liteClient } from 'algoliasearch/lite';
import { useRef, useState } from 'react';
import { InstantSearch } from 'react-instantsearch';
import { RootLoaderData } from '~/root';
import SearchInput from './Search/SearchInput';
import SearchResults from './Search/SearchResults';

interface SearchMenuProps {
	data: RootLoaderData;
}

export function SearchMenu({ data }: SearchMenuProps) {
	const { algoliaClient, algoliaSearchKey, algoliaIndexes } = data;
	const searchClient = liteClient(algoliaClient, algoliaSearchKey);
	const [searchTerm, setSearchTerm] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	return (
		<Popover className="h-full flex items-center">
			{({ open, close }) => (
				<>
					<PopoverButton
						as="button"
						className={cn(
							'flex transition-colors focus:outline-none items-center p-2 rounded-full border-transparent border lg:border-blue hover:border-blue hover:bg-blue hover:text-white',
							open && 'z-[9999] border-blue bg-blue text-white',
						)}
						onClick={() => {
							setTimeout(() => {
								inputRef.current?.focus();
							}, 0); // timeout to ensure input is rendered first
						}}
					>
						<span className="sr-only">{open ? 'Open' : 'Close'} Search</span>
						{open ? <XMarkIcon className="w-5" /> : <SearchIcon className="w-5 h-5" />}
					</PopoverButton>

					<Transition
						as="div"
						show={open}
						enter="transition ease-out duration-200"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="transition ease-in duration-150"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<PopoverBackdrop
							onClick={close}
							static
							className="fixed inset-0 h-dvh top-[3.5rem] lg:top-[6.5rem] bg-black opacity-30"
						/>
						<InstantSearch searchClient={searchClient} indexName={algoliaIndexes.products}>
							<PopoverPanel
								static
								className="fixed flex flex-col focus:outline-none inset-x-0 top-[3.5rem] lg:top-[6.5rem] border-y border-blue bg-white-pearl p-4 lg:p-8 lg:pb-0 w-full rounded-b-sm overflow-hidden"
							>
								<div className="flex items-center justify-between">
									<h4 className="lg:text-lg">Product Search</h4>
									<button
										className="cursor-pointer"
										onClick={() => {
											close();
										}}
									>
										<XMarkIcon className="w-5 h-5" />
									</button>
								</div>
								<SearchInput searchTerm={searchTerm} setSearchTerm={setSearchTerm} inputRef={inputRef} />
								<SearchResults close={close} data={data} setSearchTerm={setSearchTerm} />
							</PopoverPanel>
						</InstantSearch>
					</Transition>
				</>
			)}
		</Popover>
	);
}
